import React, { useCallback, useEffect, useState } from 'react'
import { Know } from 'interfaces/Know'
import Link from 'next/link'
import { knowRoute } from 'lib/constants/routes'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackPromise } from 'react-promise-tracker'
import { toast } from 'react-toastify'
import KnowService from 'services/KnowService'
import { ApiError } from 'services/ApiService'
import KnowDisplayBody from './KnowDisplayBody'
import { useRouter } from 'next/router'
import { isKnowNotIndexable } from 'lib/features/Know/knowSSR'

const loadingAreas = {
    know: 'knowContent',
}

interface Props {
    know: Know | null
    referrerScreen: string
    knowUuid?: string
    openInNewTabOnClick?: boolean
    onKnowClick?: (knowUuid: string) => void
    path?: string
    showTitleAsSubHeadline?: boolean
}

const KnowDisplayCard = ({
    know: initialKnow,
    knowUuid,
    referrerScreen,
    openInNewTabOnClick: openInNewTab,
    onKnowClick,
    path,
    showTitleAsSubHeadline,
}: Props) => {
    const [know, setLoadedKnow] = useState<Know | null>(initialKnow ?? null)
    const { t: tCommon } = _useTranslation('common')
    const router = useRouter()

    const knowPath = path ? path : know ? knowRoute(know!.slug, { referrerScreen }) : ''

    const fetchData = useCallback(() => {
        if (initialKnow || !knowUuid) return

        trackPromise(
            KnowService.get(knowUuid)
                .then((know) => {
                    setLoadedKnow(know)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        if (error.type === ErrorTypes.NotFound) {
                            toast.error(tCommon('common/knowNotFoundError'))
                        } else {
                            error.handleUnknownError(tCommon, 'know.get')
                        }
                    } else {
                        throw error
                    }
                }),
            loadingAreas.know
        )
    }, [initialKnow, knowUuid, tCommon])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    if (!know) return null

    const redirectToKnow = () => router.push(knowPath)

    const KnowWrapper = () =>
        isKnowNotIndexable(know) ? (
            <div onClick={redirectToKnow}>
                <KnowDisplayBody know={know} showTitleAsSubHeadline={showTitleAsSubHeadline} />
            </div>
        ) : (
            <Link href={knowPath} passHref prefetch={false} legacyBehavior>
                <a target={openInNewTab ? '_blank' : '_self'} rel="noopener noreferrer">
                    <KnowDisplayBody know={know} showTitleAsSubHeadline={showTitleAsSubHeadline} />
                </a>
            </Link>
        )

    return (
        <>
            {onKnowClick ? (
                <div onClick={() => onKnowClick(know.uuid)}>
                    <KnowDisplayBody know={know} showTitleAsSubHeadline={showTitleAsSubHeadline} />
                </div>
            ) : (
                <KnowWrapper />
            )}
        </>
    )
}

export default KnowDisplayCard
